import { head, isEmpty, map, mergeRight, transduce } from 'ramda';
import { ApiResponse } from '../@types/ApiResponse';
import dayjs from 'dayjs';
import { ViewList } from '../@types/ApiResponse/ViewList';

const byteToHex: string[] = [];

for (let n = 0; n <= 0xff; ++n) {
  const hexOctet = n.toString(16).padStart(2, '0');
  byteToHex.push(hexOctet);
}

export function Uint8ArrayToHex(buff: number[]) {
  const hexOctets = []; // new Array(buff.length) is even faster (preallocates necessary array size), then use hexOctets[i] instead of .push()

  for (let i = 0; i < buff.length; ++i) hexOctets.push(byteToHex[buff[i]]);

  return hexOctets.join('');
}

/**
 * 返回 日期属性时间差
 */
export const getDateBeforePresent = (props: {
  prop: ApiResponse.CurrentUser.TemplateProp;
  value: null | undefined | number | string;
}): {
  display: boolean;
  toString: string;
  diff: number;
  color: string;
} => {
  const res = {
    display: false,
    toString: '',
    diff: 0,
    color: '#96A1B5',
  };

  const { prop, value } = props;

  if (!prop || !value || !prop.intervalType || !prop.showInterval) return res;

  const currentTime = dayjs().valueOf();
  const normalValue = dayjs(value).valueOf();
  let prefix = '';
  let suffix = getSuffix(prop);
  let diffTime = currentTime - normalValue;

  if (currentTime > normalValue) {
    prefix = '超过';
  } else {
    prefix = '还剩';
  }

  const count = getDiff(Math.abs(diffTime), prop.intervalType);
  res.toString = `${prefix}${count}${suffix}`;
  res.display = true;
  res.diff = diffTime;
  res.color = diffTime < 0 ? '#96A1B5' : '#FF4D4F';

  return res;
};

const getSuffix = (prop: ApiResponse.CurrentUser.TemplateProp) => {
  switch (prop.intervalType) {
    case 'day':
      return '天';
    case 'hour':
      return '小时';
    case 'min':
      return '分钟';
    default:
      return '分钟';
  }
};

const getDiff = (
  diff: number,
  intervalType: ApiResponse.CurrentUser.TemplateProp['intervalType'],
) => {
  // 天/小时/分钟
  // intervalType 决定最后1个单位
  const minNum = 60;
  const hourNum = 60 * minNum;
  const dayNum = 24 * hourNum;

  switch (intervalType) {
    case 'day':
      return Math.floor(diff / 1000 / dayNum);
    case 'hour':
      return Math.floor(diff / 1000 / hourNum);
    case 'min':
      return Math.floor(diff / 1000 / minNum);
    default:
      return Math.floor(diff / 1000 / minNum);
  }
};

export const toArray = <T extends string | number = string>(v: T | T[]) =>
  Array.isArray(v) ? v : [v];

export const takeOne = <T extends any>(v: T | T[]) => (Array.isArray(v) ? v[0] : v);

export function assertNumber<T>(
  val: T | number,
  message: string | Error = 'val is not a number',
): asserts val is number {
  if (val === null || val === undefined || typeof val !== 'number') {
    if (message instanceof Error) {
      throw message;
    }
    throw new Error(message);
  }
}

export function assertExists<T>(
  val: T | null | undefined,
  message: string | Error = 'val does not exist',
): asserts val is T {
  if (val === null || val === undefined) {
    if (message instanceof Error) {
      throw message;
    }
    throw new Error(message);
  }
}

export function assertNotExists<T>(
  val: T | null | undefined,
  message = 'val exists',
): asserts val is null | undefined {
  if (val !== null && val !== undefined) {
    throw new Error(message);
  }
}

export function assertNotEmpty<T>(
  val: T | Record<string, never>,
  message = 'val empty!',
): asserts val is T {
  if (isEmpty(val)) {
    throw new Error(message);
  }
}

export function assertNormalView<T>(
  val: ViewList.ViewListItem,
  message: string | Error = '不是普通视图',
): asserts val is ViewList.ViewListItem<1 | 2 | 4 | 6> {
  if (![1, 2, 4, 6].includes(val.view_type)) {
    if (message instanceof Error) {
      throw message;
    }
    throw new Error(message);
  }
}

type Formula = {
  type: 'formula';
  formula: {
    op: string;
    input: [
      | { type: 's_prop'; prop: number }
      | { type: 'create_time' }
      | { type: 'creator' }
      | { type: 'updater' },
    ];
  };
};

export const extractFormulaInput = (formula: Formula) => {
  if (formula.type !== 'formula' || !('formula' in formula) || !('op' in formula.formula))
    return null;

  return head(formula.formula.input);
};

export const toRecord =
  <P, T extends Record<string, unknown> = Record<string, P>>(fn: (args: P) => T) =>
  (list: P[] = []) =>
    transduce<P, T, T>(map(fn), mergeRight, {} as T, list);
